import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchCartState, getPackAddDecision, getPackRulesConfig } from '@theme/pack-rules';
import { addSystemSelectionItem } from '@theme/system-selection';

const MODAL_ID = 'product-preview-modal';
const MODAL_OPEN_CLASS = 'product-preview-modal-open';
const MODAL_OPEN_EVENT = 'product-preview-modal:open';
const MODAL_BASE_WIDTH = 1380;
const MODAL_BASE_HEIGHT = 860;
const MODAL_VIEWPORT_MARGIN = 40;
const MODAL_MIN_SCALE = 0.72;

const modalScaleLengths = {
  '--product-preview-dialog-width': 1380,
  '--product-preview-dialog-height': 860,
  '--product-preview-dialog-radius': 10,
  '--product-preview-close-offset': 12,
  '--product-preview-close-size': 40,
  '--product-preview-summary-gap': 18,
  '--product-preview-summary-quality-width': 170,
  '--product-preview-summary-action-width': 220,
  '--product-preview-summary-padding-block': 18,
  '--product-preview-summary-padding-left': 24,
  '--product-preview-summary-padding-right': 68,
  '--product-preview-layout-thumb-width': 104,
  '--product-preview-layout-details-min': 320,
  '--product-preview-layout-details-max': 390,
  '--product-preview-layout-gap': 18,
  '--product-preview-layout-padding-top': 20,
  '--product-preview-layout-padding-inline': 24,
  '--product-preview-layout-padding-bottom': 24,
  '--product-preview-thumb-gap': 10,
  '--product-preview-details-padding-top': 28,
  '--product-preview-details-padding-inline': 26,
  '--product-preview-details-padding-bottom': 32,
  '--product-preview-quality-margin': 18,
  '--product-preview-badge-padding-block': 8,
  '--product-preview-badge-padding-inline': 16,
  '--product-preview-category-margin': 22,
  '--product-preview-divider-margin': 22,
  '--product-preview-desc-table-margin': 18,
  '--product-preview-shipping-gap': 10,
  '--product-preview-shipping-margin': 28,
  '--product-preview-shipping-padding-block': 12,
  '--product-preview-shipping-padding-inline': 14,
  '--product-preview-add-min-width': 188,
  '--product-preview-add-min-height': 50,
  '--product-preview-add-padding-inline': 22,
};

const modalScaleClampedLengths = {
  '--product-preview-title-size': { value: 34, min: 22, max: 34 },
  '--product-preview-badge-font-size': { value: 12, min: 11, max: 12 },
  '--product-preview-category-font-size': { value: 13, min: 12, max: 13 },
  '--product-preview-desc-font-size': { value: 15, min: 13, max: 15 },
  '--product-preview-desc-table-font-size': { value: 14, min: 12, max: 14 },
  '--product-preview-shipping-font-size': { value: 13, min: 12, max: 13 },
  '--product-preview-add-font-size': { value: 14, min: 12, max: 14 },
};

const productCache = new Map();
const productJsonCache = new Map();
let abortController = null;
let activeLoadToken = 0;
let lastActiveElement = null;
let activeMode = 'default';

function getModalElements() {
  const modal = document.getElementById(MODAL_ID);
  if (!(modal instanceof HTMLDivElement)) return null;

  const content = modal.querySelector('[data-product-preview-modal-content]');
  const closeButton = modal.querySelector('.product-preview-modal__close');
  if (!(content instanceof HTMLElement) || !(closeButton instanceof HTMLElement)) return null;

  return { modal, content, closeButton };
}

function isModalOpen(modal) {
  return modal.getAttribute('aria-hidden') === 'false';
}

function updateModalScale(modal) {
  const viewport = window.visualViewport;
  const viewportWidth = viewport?.width || window.innerWidth;
  const viewportHeight = viewport?.height || window.innerHeight;
  const availableWidth = Math.max(320, viewportWidth - MODAL_VIEWPORT_MARGIN);
  const availableHeight = Math.max(320, viewportHeight - MODAL_VIEWPORT_MARGIN);
  const scale = Math.max(
    MODAL_MIN_SCALE,
    Math.min(1, availableWidth / MODAL_BASE_WIDTH, availableHeight / MODAL_BASE_HEIGHT)
  );

  modal.style.setProperty('--product-preview-scale', scale.toFixed(3));

  Object.entries(modalScaleLengths).forEach(([property, value]) => {
    modal.style.setProperty(property, `${(value * scale).toFixed(2)}px`);
  });

  Object.entries(modalScaleClampedLengths).forEach(([property, config]) => {
    const value = Math.max(config.min, Math.min(config.max, config.value * scale));
    modal.style.setProperty(property, `${value.toFixed(2)}px`);
  });
}

function normalizeProductUrl(rawUrl) {
  if (!rawUrl) return null;

  try {
    const url = new URL(String(rawUrl), window.location.origin);
    if (url.origin !== window.location.origin) return null;
    url.hash = '';
    return url;
  } catch (_error) {
    return null;
  }
}

function renderState(content, message, modifier = '') {
  const state = document.createElement('div');
  state.className = `product-preview-modal__state${modifier ? ` ${modifier}` : ''}`;
  state.textContent = message;
  content.replaceChildren(state);
}

function cloneImage(sourceImage, className, sizes) {
  const image = document.createElement('img');
  image.className = className;
  image.alt = sourceImage.getAttribute('alt') || '';
  image.loading = 'lazy';
  image.decoding = 'async';

  const src = sourceImage.getAttribute('src');
  const srcset = sourceImage.getAttribute('srcset');
  if (src) image.src = src;
  if (srcset) image.srcset = srcset;
  image.sizes = sizes;

  return image;
}

function getProductImages(sourceGrid) {
  const images = [];
  const seen = new Set();
  const sourceImages = sourceGrid.querySelectorAll(
    '.product-information__media .product-media[data-media-id] > img.product-media__image'
  );

  sourceImages.forEach((image) => {
    if (!(image instanceof HTMLImageElement) || image.closest('zoom-dialog')) return;

    const media = image.closest('.product-media[data-media-id]');
    const key = media?.getAttribute('data-media-id') || image.getAttribute('src') || image.getAttribute('srcset');
    if (!key || seen.has(key)) return;

    seen.add(key);
    images.push(image);
  });

  return images;
}

function createProductGallery(sourceGrid) {
  const images = getProductImages(sourceGrid);
  if (images.length === 0) return null;

  const layout = document.createElement('section');
  layout.className = 'product-preview-modal__layout';
  layout.setAttribute('aria-label', 'Detalle visual del producto');

  const thumbs = document.createElement('div');
  thumbs.className = 'product-preview-modal__thumbs';

  const main = document.createElement('figure');
  main.className = 'product-preview-modal__main-media';

  const mainImage = cloneImage(images[0], 'product-preview-modal__main-image', '(min-width: 750px) 960px, 100vw');
  mainImage.loading = 'eager';
  mainImage.setAttribute('data-product-preview-main-image', 'true');
  main.appendChild(mainImage);

  images.forEach((image, index) => {
    const thumb = document.createElement('button');
    thumb.type = 'button';
    thumb.className = 'product-preview-modal__thumb';
    thumb.dataset.productPreviewThumb = String(index);
    thumb.dataset.mainSrc = image.getAttribute('src') || '';
    thumb.dataset.mainSrcset = image.getAttribute('srcset') || '';
    thumb.dataset.mainAlt = image.getAttribute('alt') || '';
    thumb.setAttribute('aria-label', `Ver imagen ${index + 1}`);
    thumb.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    if (index === 0) thumb.classList.add('is-selected');

    thumb.appendChild(cloneImage(image, 'product-preview-modal__thumb-image', '96px'));
    thumbs.appendChild(thumb);
  });

  layout.appendChild(thumbs);
  layout.appendChild(main);

  return layout;
}

function addQualityClass(details) {
  const badge = details.querySelector('.dr-calidad-badge');
  if (!(badge instanceof HTMLElement)) return;

  const rawText = badge.textContent || '';
  const normalized = rawText
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();

  if (normalized.includes('MEDIA')) {
    badge.classList.add('dr-calidad-badge--media');
  } else if (normalized.includes('ESTANDAR')) {
    badge.classList.add('dr-calidad-badge--estandar');
  } else if (normalized.includes('ALTA')) {
    badge.classList.add('dr-calidad-badge--alta');
  } else if (normalized.includes('PREMIUM')) {
    badge.classList.add('dr-calidad-badge--premium');
  }
}

function createProductActions(variantData) {
  const actions = document.createElement('div');
  actions.className = 'product-preview-modal__actions';

  const error = document.createElement('p');
  error.className = 'product-preview-modal__error';
  error.hidden = true;
  error.setAttribute('data-product-preview-add-error', 'true');

  const addButton = document.createElement('button');
  addButton.type = 'button';
  addButton.className = 'button product-preview-modal__add-button';
  addButton.textContent = variantData?.available === false ? 'No disponible' : 'Añadir a Mi selección';
  addButton.dataset.productPreviewAdd = 'true';
  if (variantData?.id) addButton.dataset.variantId = variantData.id;
  if (variantData?.sku) addButton.dataset.productSku = variantData.sku;
  if (variantData?.productId) addButton.dataset.productId = variantData.productId;
  if (!variantData?.id || variantData?.available === false) addButton.disabled = true;

  actions.appendChild(addButton);
  actions.appendChild(error);

  return actions;
}

function createProductDetails(sourceGrid, variantData, options = {}) {
  const sourceDetails = sourceGrid.querySelector('.product-details');
  if (!(sourceDetails instanceof HTMLElement)) {
    throw new Error('Product details not found');
  }

  const details = sourceDetails.cloneNode(true);
  if (!(details instanceof HTMLElement)) {
    throw new Error('Product details not available');
  }

  details.classList.add('product-preview-modal__details');
  details
    .querySelectorAll('.dr-return, [data-product-return-to-arma], .view-more-details__wrapper, script')
    .forEach((node) => {
      node.remove();
    });
  details.querySelectorAll('style').forEach((styleElement) => {
    if (styleElement.textContent?.includes('.dr-col')) styleElement.remove();
  });

  addQualityClass(details);

  if (options.readonly !== true) {
    const actions = createProductActions(variantData);
    const detailsBody = details.querySelector('.dr-col');
    if (detailsBody instanceof HTMLElement) {
      detailsBody.appendChild(actions);
    } else {
      details.appendChild(actions);
    }
  }

  return details;
}

function productJsonUrl(productUrl) {
  const url = new URL(productUrl.href);
  url.search = '';
  url.hash = '';
  url.pathname = url.pathname.replace(/\/$/, '') + '.js';
  return url;
}

async function fetchProductJson(productUrl) {
  const url = productJsonUrl(productUrl);
  const cacheKey = url.href;
  if (productJsonCache.has(cacheKey)) return productJsonCache.get(cacheKey);

  const response = await fetch(url.href, {
    credentials: 'same-origin',
  });

  if (!response.ok) throw new Error(`Failed to fetch product JSON: HTTP ${response.status}`);

  const product = await response.json();
  productJsonCache.set(cacheKey, product);
  return product;
}

async function resolveVariantData(productUrl) {
  const requestedVariantId = productUrl.searchParams.get('variant');

  try {
    const product = await fetchProductJson(productUrl);
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const variant =
      variants.find((item) => String(item?.id) === String(requestedVariantId || '')) ||
      variants.find((item) => item?.available) ||
      variants[0];

    if (!variant?.id) return null;

    return {
      id: String(variant.id),
      sku: String(variant.sku || ''),
      available: variant.available !== false,
      productId: product?.id ? String(product.id) : '',
    };
  } catch (_error) {
    if (!requestedVariantId) return null;
    return {
      id: String(requestedVariantId),
      sku: '',
      available: true,
      productId: '',
    };
  }
}

async function prepareProductContent(documentFragment, productUrl, options = {}) {
  const sourceGrid = documentFragment.querySelector('[data-product-grid-content]');
  if (!(sourceGrid instanceof HTMLElement)) {
    throw new Error('Product content not found');
  }

  const variantData = await resolveVariantData(productUrl);
  const product = document.createElement('article');
  product.className = 'product-preview-modal__product';
  product.dataset.productUrl = productUrl.href;

  const details = createProductDetails(sourceGrid, variantData, options);
  const layout = createProductGallery(sourceGrid);
  if (layout) {
    layout.appendChild(details);
    product.appendChild(layout);
  } else {
    details.classList.add('product-preview-modal__details--full');
    product.appendChild(details);
  }

  return product;
}

async function fetchProductGrid(productUrl, options = {}) {
  const cacheKey = `${productUrl.href}::${options.readonly === true ? 'readonly' : 'default'}`;
  const cached = productCache.get(cacheKey);
  if (cached instanceof HTMLElement) return cached.cloneNode(true);

  abortController?.abort();
  abortController = new AbortController();

  const response = await fetch(productUrl.href, {
    credentials: 'same-origin',
    signal: abortController.signal,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch product page: HTTP ${response.status}`);
  }

  const responseText = await response.text();
  const productDocument = new DOMParser().parseFromString(responseText, 'text/html');
  const productGrid = await prepareProductContent(productDocument, productUrl, options);
  productCache.set(cacheKey, productGrid.cloneNode(true));

  return productGrid;
}

function openShell(elements) {
  const { modal, content, closeButton } = elements;
  const wasOpen = isModalOpen(modal);

  if (!wasOpen) {
    lastActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }
  updateModalScale(modal);
  modal.setAttribute('aria-hidden', 'false');
  document.body.classList.add(MODAL_OPEN_CLASS);
  renderState(content, 'Cargando producto...');

  requestAnimationFrame(() => {
    closeButton.focus({ preventScroll: true });
  });
}

function closeModal() {
  const elements = getModalElements();
  if (!elements) return;

  activeLoadToken += 1;
  abortController?.abort();
  abortController = null;

  elements.modal.setAttribute('aria-hidden', 'true');
  elements.modal.removeAttribute('data-product-preview-mode');
  elements.modal.style.removeProperty('--product-preview-scale');
  elements.content.replaceChildren();
  document.body.classList.remove(MODAL_OPEN_CLASS);

  if (lastActiveElement?.isConnected) {
    lastActiveElement.focus({ preventScroll: true });
  }
  lastActiveElement = null;
}

function selectGalleryImage(thumb) {
  const layout = thumb.closest('.product-preview-modal__layout');
  const mainImage = layout?.querySelector('[data-product-preview-main-image]');
  if (!(layout instanceof HTMLElement) || !(mainImage instanceof HTMLImageElement)) return;

  layout.querySelectorAll('[data-product-preview-thumb]').forEach((button) => {
    const isSelected = button === thumb;
    button.classList.toggle('is-selected', isSelected);
    button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
  });

  const src = thumb.dataset.mainSrc || '';
  const srcset = thumb.dataset.mainSrcset || '';
  const alt = thumb.dataset.mainAlt || '';

  if (src) mainImage.src = src;
  if (srcset) {
    mainImage.srcset = srcset;
  } else {
    mainImage.removeAttribute('srcset');
  }
  mainImage.alt = alt;
}

function setAddButtonState(button, state, message = '') {
  const error = button.closest('.product-preview-modal__product')?.querySelector('[data-product-preview-add-error]');
  if (error instanceof HTMLElement) {
    error.hidden = !message;
    error.textContent = message;
  }

  if (state === 'loading') {
    button.disabled = true;
    button.textContent = 'Agregando...';
    return;
  }

  if (state === 'idle') {
    button.disabled = false;
    button.textContent = 'Añadir a Mi selección';
  }
}

async function getPackDecision(sku) {
  if (!sku) return { allowed: true, quantity: 1 };

  try {
    const [cart, config] = await Promise.all([fetchCartState(), getPackRulesConfig()]);
    return getPackAddDecision({
      sku,
      requestedQuantity: 1,
      cartItems: cart.items || [],
      config,
      canAdjustQuantity: true,
    });
  } catch (error) {
    console.warn('[pack-rules] No se pudo validar el producto antes de agregarlo.', error);
    return { allowed: true, quantity: 1 };
  }
}

async function addProductToCart(button) {
  const variantId = button.dataset.variantId || '';
  if (!variantId) return;

  setAddButtonState(button, 'loading');

  try {
    const decision = await getPackDecision(button.dataset.productSku || '');
    if (!decision.allowed) {
      setAddButtonState(button, 'idle', decision.message || 'No se puede agregar este producto a Mi selección.');
      document.dispatchEvent(
        new CartErrorEvent('product-preview-modal', decision.message || 'No se puede agregar este producto.', null, null)
      );
      return;
    }

    const quantity = Number.isInteger(decision.quantity) && decision.quantity > 0 ? decision.quantity : 1;
    const cartOrder = String(Date.now());
    const sku = String(button.dataset.productSku || '').trim();
    if (!sku) throw new Error('Este producto no tiene un SKU válido.');
    const state = await addSystemSelectionItem(
      { sku, qty: quantity, order: Number(cartOrder), source: 'CATALOGO' },
      { source: 'CATALOGO' }
    );

    document.dispatchEvent(
      new CartAddEvent(state, variantId, {
        source: 'product-form-component',
        action: 'add',
        itemCount: Number(state.item_count || 0),
        itemCountIsTotal: true,
        productId: button.dataset.productId || '',
        variantId,
        cartOrder,
      })
    );

    closeModal();
  } catch (error) {
    const message = error?.message || 'No se pudo agregar el producto.';
    setAddButtonState(button, 'idle', message);
    document.dispatchEvent(new CartErrorEvent('product-preview-modal', message, message, null));
  }
}

async function openProductPreview(rawUrl, options = {}) {
  const elements = getModalElements();
  const productUrl = normalizeProductUrl(rawUrl);
  if (!elements || !productUrl) return false;

  activeMode = options.mode === 'readonly' ? 'readonly' : 'default';
  elements.modal.dataset.productPreviewMode = activeMode;
  const loadToken = activeLoadToken + 1;
  activeLoadToken = loadToken;
  openShell(elements);

  try {
    const productGrid = await fetchProductGrid(productUrl, { readonly: activeMode === 'readonly' });
    if (activeLoadToken !== loadToken || !isModalOpen(elements.modal)) return true;

    elements.content.replaceChildren(productGrid);
    return true;
  } catch (error) {
    if (error?.name === 'AbortError') return true;
    if (activeLoadToken === loadToken && isModalOpen(elements.modal)) {
      renderState(elements.content, 'No pudimos cargar el producto.', 'product-preview-modal__state--error');
    }
    return true;
  }
}

function handleOpenEvent(event) {
  const productUrl = event.detail?.url;
  if (!getModalElements() || !normalizeProductUrl(productUrl)) return;

  event.preventDefault();
  void openProductPreview(productUrl, { mode: event.detail?.mode });
}

function handleModalClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const thumb = target.closest('[data-product-preview-thumb]');
  if (thumb instanceof HTMLButtonElement) {
    event.preventDefault();
    selectGalleryImage(thumb);
    return;
  }

  const addButton = target.closest('[data-product-preview-add]');
  if (addButton instanceof HTMLButtonElement) {
    event.preventDefault();
    void addProductToCart(addButton);
    return;
  }

  if (target.closest('[data-product-preview-modal-close]')) {
    event.preventDefault();
    closeModal();
    return;
  }

  const productLink = target.closest('a[href]');
  if (!(productLink instanceof HTMLAnchorElement)) return;

  const productUrl = normalizeProductUrl(productLink.href);
  if (!productUrl || !productUrl.pathname.startsWith('/products/')) return;

  event.preventDefault();
  void openProductPreview(productUrl.href);
}

function handleKeydown(event) {
  if (event.key !== 'Escape') return;

  const elements = getModalElements();
  if (!elements || !isModalOpen(elements.modal)) return;

  event.preventDefault();
  closeModal();
}

function handleResize() {
  const elements = getModalElements();
  if (!elements || !isModalOpen(elements.modal)) return;

  updateModalScale(elements.modal);
}

function bindModalHandlers() {
  const elements = getModalElements();
  elements?.modal.addEventListener('click', handleModalClick);
}

document.addEventListener(MODAL_OPEN_EVENT, handleOpenEvent);
document.addEventListener('keydown', handleKeydown);
window.addEventListener('resize', handleResize);
window.visualViewport?.addEventListener('resize', handleResize);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindModalHandlers, { once: true });
} else {
  bindModalHandlers();
}
